import { Controller } from "@hotwired/stimulus"

export default class extends Controller<HTMLElement> {
  static targets = ["mainImage", "thumbnail", "counter", "swipeArea", "dot"]

  declare readonly mainImageTarget: HTMLImageElement
  declare readonly mainImageTargets: HTMLImageElement[]
  declare readonly thumbnailTargets: HTMLElement[]
  declare readonly counterTarget: HTMLElement
  declare readonly swipeAreaTarget: HTMLElement
  declare readonly dotTargets: HTMLElement[]
  declare readonly hasMainImageTarget: boolean
  declare readonly hasCounterTarget: boolean
  declare readonly hasSwipeAreaTarget: boolean

  currentIndex: number = 0

  // Touch swipe tracking
  private touchStartX: number = 0
  private touchStartY: number = 0
  private touchEndX: number = 0
  private touchEndY: number = 0
  private isSwiping: boolean = false
  private transitionTimeout: number | null = null
  private activeImageIndex: number = 0

  connect(): void {
    this.currentIndex = 0
    this.updateCounter()
    this.updateDots()
    this.setupTouchHandlers()
  }

  disconnect(): void {
    this.removeTouchHandlers()
  }

  private setupTouchHandlers(): void {
    const swipeElement = this.hasSwipeAreaTarget ? this.swipeAreaTarget : this.element

    // Bind methods once to ensure add/remove works correctly
    this.handleTouchStart = this.handleTouchStart.bind(this)
    this.handleTouchMove = this.handleTouchMove.bind(this)
    this.handleTouchEnd = this.handleTouchEnd.bind(this)

    swipeElement.addEventListener("touchstart", this.handleTouchStart, { passive: true })
    swipeElement.addEventListener("touchmove", this.handleTouchMove, { passive: false })
    swipeElement.addEventListener("touchend", this.handleTouchEnd, { passive: true })
  }

  private removeTouchHandlers(): void {
    const swipeElement = this.hasSwipeAreaTarget ? this.swipeAreaTarget : this.element

    swipeElement.removeEventListener("touchstart", this.handleTouchStart)
    swipeElement.removeEventListener("touchmove", this.handleTouchMove)
    swipeElement.removeEventListener("touchend", this.handleTouchEnd)
  }

  handleTouchStart(event: TouchEvent): void {
    this.touchStartX = event.touches[0].clientX
    this.touchStartY = event.touches[0].clientY
    // Initialize end coordinates to start coordinates to prevent false positives if no move occurs
    this.touchEndX = this.touchStartX
    this.touchEndY = this.touchStartY
    this.isSwiping = false
  }

  handleTouchMove(event: TouchEvent): void {
    if (this.touchStartX === 0 && this.touchStartY === 0) return

    this.touchEndX = event.touches[0].clientX
    this.touchEndY = event.touches[0].clientY

    const diffX = this.touchStartX - this.touchEndX
    const diffY = this.touchStartY - this.touchEndY

    // Once swiping is detected, keep it as swiping
    if (this.isSwiping) {
      if (event.cancelable) event.preventDefault()
      return
    }

    // Determine if this is a horizontal swipe gesture
    // Horizontal distance must be greater than vertical distance and exceed threshold
    if (Math.abs(diffX) > Math.abs(diffY) && Math.abs(diffX) > 10) {
      this.isSwiping = true
      if (event.cancelable) event.preventDefault()
    }
  }

  handleTouchEnd(event: TouchEvent): void {
    // Get final position from changedTouches
    if (event.changedTouches && event.changedTouches.length > 0) {
      this.touchEndX = event.changedTouches[0].clientX
      this.touchEndY = event.changedTouches[0].clientY
    }

    const diffX = this.touchStartX - this.touchEndX
    const minSwipeDistance = 50

    // Only trigger if we detected a swipe intent or a significant horizontal move
    if (this.isSwiping || Math.abs(diffX) > minSwipeDistance) {
      if (Math.abs(diffX) > minSwipeDistance) {
        if (diffX > 0) {
          this.next()
        } else {
          this.prev()
        }
      }
    }

    // Reset values
    this.touchStartX = 0
    this.touchStartY = 0
    this.touchEndX = 0
    this.touchEndY = 0
    this.isSwiping = false
  }

  selectImage(event: Event): void {
    const button = event.currentTarget as HTMLElement
    const imageUrl = button.dataset.imageUrl
    const index = this.thumbnailTargets.indexOf(button)

    if (index !== -1) {
      this.currentIndex = index
    }

    this.updateMainImage(imageUrl)
    this.updateThumbnailSelection(button)
    this.updateCounter()
  }

  prev(): void {
    if (this.thumbnailTargets.length === 0) return

    const newIndex = this.currentIndex > 0
      ? this.currentIndex - 1
      : this.thumbnailTargets.length - 1

    this.goToIndex(newIndex)
  }

  next(): void {
    if (this.thumbnailTargets.length === 0) return

    const newIndex = this.currentIndex < this.thumbnailTargets.length - 1
      ? this.currentIndex + 1
      : 0

    this.goToIndex(newIndex)
  }

  private goToIndex(index: number): void {
    const thumbnail = this.thumbnailTargets[index]
    if (!thumbnail) return

    this.currentIndex = index
    const imageUrl = thumbnail.dataset.imageUrl
    this.updateMainImage(imageUrl)
    this.updateThumbnailSelection(thumbnail)
    this.updateCounter()

    // Scroll thumbnail into view
    thumbnail.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })

    this.updateDots()
  }

  private updateMainImage(imageUrl: string | undefined): void {
    if (this.mainImageTargets.length < 2 || !imageUrl) return

    if (this.transitionTimeout) {
      clearTimeout(this.transitionTimeout)
    }

    // Determine indices
    const nextIndex = (this.activeImageIndex + 1) % 2
    const currentImg = this.mainImageTargets[this.activeImageIndex] as HTMLImageElement
    const nextImg = this.mainImageTargets[nextIndex] as HTMLImageElement

    // Prepare Next Image
    nextImg.src = imageUrl

    // Bring Next to Front (z-20) and Fade In
    nextImg.style.zIndex = "20"

    requestAnimationFrame(() => {
      nextImg.style.opacity = "1"
    })

    this.transitionTimeout = window.setTimeout(() => {
      // Hide Old Image (reset to back)
      currentImg.style.opacity = "0"
      currentImg.style.zIndex = "0"

      // Standardize Next Image z-index (z-10 is the active level)
      nextImg.style.zIndex = "10"

      this.transitionTimeout = null
    }, 500)

    // Update active index
    this.activeImageIndex = nextIndex
  }

  private updateThumbnailSelection(selected: HTMLElement): void {
    this.thumbnailTargets.forEach(thumb => {
      thumb.classList.remove("border-carbot-pink", "ring-2", "ring-carbot-pink/30")
      thumb.classList.add("border-white/10")
    })

    selected.classList.remove("border-white/10")
    selected.classList.add("border-carbot-pink", "ring-2", "ring-carbot-pink/30")
  }

  private updateCounter(): void {
    if (this.hasCounterTarget && this.thumbnailTargets.length > 0) {
      this.counterTarget.textContent = `${this.currentIndex + 1} / ${this.thumbnailTargets.length}`
    }
  }

  selectDot(event: Event): void {
    const dot = event.currentTarget as HTMLElement
    const index = parseInt(dot.dataset.index || "0")
    if (!isNaN(index)) {
      this.goToIndex(index)
    }
  }

  private updateDots(): void {
    this.dotTargets.forEach((dot, index) => {
      if (index === this.currentIndex) {
        dot.classList.add("bg-white", "w-6")
        dot.classList.remove("bg-white/30")
      } else {
        dot.classList.remove("bg-white", "w-6")
        dot.classList.add("bg-white/30")
      }
    })
  }
}
